import React, { useState } from 'react';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';

import { useStore } from 'core/store';
import { Loading } from 'core/components/loading';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import Section from 'core/components/section';

const BRANCH_DATA_SECTION = { id: 'branch-data', label: 'Branch Data Changes' };
const CD_REQUEST_SECTION = { id: 'cd-request', label: 'ClarionDoor Request XML' };

const useStyles = makeStyles((theme) => ({
  navBar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    alignItems: 'center'
  },
  jumpToLabel: {
    marginRight: theme.spacing(1)
  }
}));

const getInitialAccumulatorValue = (removedChanges, addedChanges) => {
  const obj = {};
  Object.keys(removedChanges).forEach((key) => {
    obj[key] = null;
  });
  Object.keys(addedChanges).forEach((key) => {
    obj[key] = null;
  });
  return obj;
};

const sortedObj = (obj, defaults) =>
  Object.keys(obj)
    .sort()
    .reduce((accumulator, key) => {
      accumulator[key] = obj[key];
      return accumulator;
    }, defaults);

const PreviewClarionDoorRequestDifference = ({
  policyPreviewId,
  policyId,
  cdIdBefore,
  cdIdAfter,
  hideBranchDataDiff = false,
  beforeTitle,
  afterTitle
}) => {
  const classes = useStyles();
  const {
    account: {
      policies: {
        policy: { getClarionDoorRequestDiff }
      }
    }
  } = useStore();

  const [data, setData] = useState();
  const [gqlError, setGqlError] = useState();
  const [loading, setLoading] = useState(false);

  // Two fetch modes:
  // - cdId path (version-compare): diff two arbitrary ClarionDoor request IDs. No
  //   Branch-data diff is produced on this path, so the caller sets hideBranchDataDiff.
  // - preview path (policy preview): diff the before/after of a single preview.
  const useCdIdPath = Boolean(cdIdBefore && cdIdAfter);

  // Guard on gqlError too: on error we set gqlError but never set data, so without this the
  // render-time fetch would re-fire every render and hammer the backend in an infinite loop.
  if (!loading && !data && !gqlError) {
    setLoading(true);
    const args = useCdIdPath
      ? { cdIdBefore, cdIdAfter, invokedBy: 'staff-fe-version-compare' }
      : { policyId, policyPreviewId };
    getClarionDoorRequestDiff(args)
      .then(({ requests, error }) => {
        setLoading(false);
        if (requests) {
          // `diff` (Branch-data changes) is only populated on the preview path; it's
          // null on the cdId path. `{}` is not valid JSON, so guard before parsing.
          setData({ ...requests, diff: requests?.diff ? JSON.parse(requests.diff) : null });
        }
        if (error) {
          setGqlError(error);
        }
      })
      .catch((e) => {
        // The store action re-throws on Apollo/network errors; without this the spinner
        // would hang forever with no error surfaced.
        setLoading(false);
        setGqlError(e?.message ?? 'Unexpected error loading ClarionDoor diff');
      });
  }

  const sortedRemovedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.removedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );
  const sortedAddedChanges =
    data?.diff &&
    sortedObj(
      data?.diff.full.addedChanges,
      getInitialAccumulatorValue(data?.diff.full.removedChanges, data?.diff.full.addedChanges)
    );

  if (loading) {
    return <Loading />;
  }

  const hasRequestXml = data?.xmlBeforePreview?.success && data?.xmlAfterPreview?.success;

  const sections = hideBranchDataDiff ? [CD_REQUEST_SECTION] : [BRANCH_DATA_SECTION, CD_REQUEST_SECTION];

  const leftXmlTitle = beforeTitle ?? 'ClarionDoor Request Before Preview';
  const rightXmlTitle = afterTitle ?? 'ClarionDoor Request After Preview';

  return (
    <>
      {/* The "Jump to" nav only earns its keep when there's more than one section to jump between.
          On the version-compare path there's a single section, so the nav would just anchor to the
          content directly below it — hide it there. */}
      {sections.length > 1 && (
        <Grid container spacing={2} className={classes.navBar}>
          <Grid item>
            <Label type="darkGreenSmallBold" className={classes.jumpToLabel}>
              Jump to:
            </Label>
          </Grid>
          {sections.map(({ id, label }) => (
            <Grid item key={id}>
              <Button variant="contained" color="secondary" href={`#${id}`}>
                {label}
              </Button>
            </Grid>
          ))}
        </Grid>
      )}

      {!hideBranchDataDiff && (
        <Section title="Branch Data Changes" type="Section" id="branch-data">
          {data?.diff ? (
            <ReactDiffViewer
              oldValue={JSON.stringify(sortedRemovedChanges, null, 4)}
              newValue={JSON.stringify(sortedAddedChanges, null, 4)}
              splitView
              compareMethod={DiffMethod.WORDS}
              leftTitle={'Branch Data Before Preview'}
              rightTitle={'Branch Data After Preview'}
            />
          ) : (
            <>
              <Label>Error getting Branch Data diff.</Label>
              {gqlError && <Label>{gqlError}</Label>}
            </>
          )}
        </Section>
      )}

      <Section title="ClarionDoor Request XML" type="Section" id="cd-request">
        {hasRequestXml ? (
          <ReactDiffViewer
            oldValue={data.xmlBeforePreview.data}
            newValue={data.xmlAfterPreview.data}
            splitView
            compareMethod={DiffMethod.WORDS}
            leftTitle={`${leftXmlTitle} - ID: ${data?.requestIdBeforePreview}`}
            rightTitle={`${rightXmlTitle} - ID: ${data?.requestIdAfterPreview}`}
          />
        ) : (
          <>
            <Label>Error getting ClarionDoor request data.</Label>
            <Label>{data?.xmlBeforePreview?.message || data?.xmlAfterPreview?.message || gqlError}</Label>
          </>
        )}
      </Section>
    </>
  );
};

export default PreviewClarionDoorRequestDifference;
